import { useRef, useState } from 'react'
import { downloadFile } from '../api/client'
import {
  useDeleteProjectFile,
  useProjectFiles,
  useUpdateProjectFile,
  useUploadProjectFile
} from '../api/queries'
import type { FileCategory, ProjectFile } from '../api/types'
import { fileCategoryLabel, fileCategoryLabels } from '../api/types'
import { useAuth } from '../auth/AuthProvider'
import { formatFileSize, prepareDocumentFile } from '../lib/documentPhoto'
import { formatDateTime } from '../lib/format'
import { useAuthedBlob } from '../lib/photos'
import { Card, EmptyState } from './ui'

/**
 * Dokumentation på en sag: før/efter-billeder, tavlebilleder, kvitteringer og
 * tegninger.
 *
 * Filerne ligger i sagens kanal i Microsoft Teams - ikke på serveren - så de er
 * de samme filer, folk ser under fanen "Filer" i Teams (beslutning 023).
 * Alle der kan se sagen, kan lægge dokumentation på den: det er montøren på
 * stedet, der tager billedet.
 */
export function ProjectFilesCard({ projectId }: { projectId: number }) {
  const { data: files, isLoading } = useProjectFiles(projectId)
  const upload = useUploadProjectFile(projectId)

  const cameraInput = useRef<HTMLInputElement>(null)
  const fileInput = useRef<HTMLInputElement>(null)

  const [category, setCategory] = useState<FileCategory>('before')
  const [filter, setFilter] = useState<FileCategory | 'all'>('all')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [viewing, setViewing] = useState<ProjectFile | null>(null)

  async function onFilesSelected(event: React.ChangeEvent<HTMLInputElement>) {
    const selected = Array.from(event.target.files ?? [])
    event.target.value = ''
    if (selected.length === 0) return

    setError(null)
    setBusy(true)

    try {
      // Én ad gangen: den første fejl (typisk manglende Teams-adgang) rammer
      // også resten, og så er der ingen grund til at vente på dem alle.
      for (const file of selected) {
        const prepared = await prepareDocumentFile(file)
        await upload.mutateAsync({
          file: prepared.blob,
          fileName: prepared.fileName,
          category
        })
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Filen kunne ikke gemmes.')
    } finally {
      setBusy(false)
    }
  }

  const shown = (files ?? []).filter((f) => filter === 'all' || f.category === filter)
  const images = shown.filter((f) => f.isImage)
  const documents = shown.filter((f) => !f.isImage)

  return (
    <Card
      title={`Dokumentation${files?.length ? ` (${files.length})` : ''}`}
      actions={
        <select
          style={{ maxWidth: 150 }}
          value={filter}
          onChange={(event) => setFilter(event.target.value as FileCategory | 'all')}
        >
          <option value="all">Alle</option>
          {fileCategoryLabels.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>
      }
    >
      <div className="row" style={{ flexWrap: 'wrap', marginBottom: 14 }}>
        <select
          style={{ maxWidth: 130 }}
          value={category}
          onChange={(event) => setCategory(event.target.value as FileCategory)}
          aria-label="Kategori for det næste billede"
        >
          {fileCategoryLabels.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>

        {/* capture="environment" åbner bagkameraet direkte på mobilen. */}
        <input
          ref={cameraInput}
          type="file"
          accept="image/*"
          capture="environment"
          multiple
          hidden
          onChange={onFilesSelected}
        />
        <input ref={fileInput} type="file" multiple hidden onChange={onFilesSelected} />

        <button
          className="btn btn--primary btn--sm"
          disabled={busy}
          onClick={() => cameraInput.current?.click()}
        >
          {busy ? 'Gemmer ...' : 'Tag billede'}
        </button>
        <button className="btn btn--sm" disabled={busy} onClick={() => fileInput.current?.click()}>
          Vælg fil
        </button>

        <span className="tiny muted">Gemmes i sagens mappe i SharePoint</span>
      </div>

      {error && <div className="error-box" style={{ marginBottom: 12 }}>{error}</div>}

      {isLoading ? (
        <div className="muted small">Henter ...</div>
      ) : shown.length === 0 ? (
        <EmptyState>
          {filter === 'all'
            ? 'Der er ingen dokumentation på sagen endnu.'
            : 'Ingen filer i den valgte kategori.'}
        </EmptyState>
      ) : (
        <>
          {images.length > 0 && (
            <div className="file-grid">
              {images.map((file) => (
                <FileThumbnail
                  key={file.id}
                  projectId={projectId}
                  file={file}
                  onOpen={() => setViewing(file)}
                />
              ))}
            </div>
          )}

          {documents.length > 0 && (
            <div className="stack" style={{ marginTop: images.length > 0 ? 14 : 0 }}>
              {documents.map((file) => (
                <DocumentRow key={file.id} projectId={projectId} file={file} />
              ))}
            </div>
          )}
        </>
      )}

      {viewing && (
        <FileViewer projectId={projectId} file={viewing} onClose={() => setViewing(null)} />
      )}
    </Card>
  )
}

/** Ét billede i galleriet. Miniaturen laves af SharePoint. */
function FileThumbnail({
  projectId,
  file,
  onOpen
}: {
  projectId: number
  file: ProjectFile
  onOpen: () => void
}) {
  const thumbnail = useAuthedBlob(`/api/projects/${projectId}/files/${file.id}/thumbnail`)
  const label = fileCategoryLabel(file.category)

  return (
    <button className="file-thumb" onClick={onOpen} title={file.caption ?? file.name}>
      {thumbnail ? (
        <img src={thumbnail} alt={file.caption ?? file.name} loading="lazy" />
      ) : (
        <div className="file-thumb__placeholder" />
      )}
      <span className="file-thumb__label">{label}</span>
    </button>
  )
}

/** En fil der ikke kan vises som billede - PDF, tegning, regneark. */
function DocumentRow({ projectId, file }: { projectId: number; file: ProjectFile }) {
  const label = fileCategoryLabel(file.category)

  return (
    <div className="row row--between" style={{ gap: 10 }}>
      <div style={{ minWidth: 0 }}>
        <div className="small" style={{ overflowWrap: 'anywhere' }}>
          {file.name}
        </div>
        <div className="tiny muted">
          {label} · {formatFileSize(file.sizeBytes)} · {file.uploadedBy?.displayName ?? 'Ukendt'} ·{' '}
          {formatDateTime(file.uploadedAt)}
        </div>
      </div>
      {/* Knap frem for link: API-et kræver et Authorization-header, som et
          almindeligt <a href> ikke sender. */}
      <button
        className="btn btn--sm"
        onClick={() =>
          void downloadFile(`/api/projects/${projectId}/files/${file.id}/content`, file.name)
        }
      >
        Åbn
      </button>
    </div>
  )
}

/** Billedet i fuld størrelse med billedtekst, kategori og sletning. */
function FileViewer({
  projectId,
  file,
  onClose
}: {
  projectId: number
  file: ProjectFile
  onClose: () => void
}) {
  const { user, isAdmin } = useAuth()
  const full = useAuthedBlob(`/api/projects/${projectId}/files/${file.id}/content`)
  const update = useUpdateProjectFile(projectId)
  const remove = useDeleteProjectFile(projectId)

  const [caption, setCaption] = useState(file.caption ?? '')
  const [category, setCategory] = useState<FileCategory>(file.category)
  const [error, setError] = useState<string | null>(null)

  // Den der lagde filen op må rette den; det samme må administratorer.
  const mayChange = isAdmin || file.uploadedBy?.id === user?.id

  async function save() {
    setError(null)
    try {
      await update.mutateAsync({ fileId: file.id, caption, category })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ændringen kunne ikke gemmes.')
    }
  }

  async function deleteFile() {
    setError(null)
    try {
      await remove.mutateAsync(file.id)
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Filen kunne ikke slettes.')
    }
  }

  return (
    <div className="file-viewer" role="dialog" aria-label={file.name} onClick={onClose}>
      <div className="file-viewer__inner" onClick={(event) => event.stopPropagation()}>
        {full ? (
          <img className="file-viewer__image" src={full} alt={file.caption ?? file.name} />
        ) : (
          <div className="file-viewer__loading">Henter billedet ...</div>
        )}

        <div className="file-viewer__panel">
          <div className="tiny muted" style={{ marginBottom: 8 }}>
            {file.uploadedBy?.displayName ?? 'Ukendt'} · {formatDateTime(file.uploadedAt)} ·{' '}
            {formatFileSize(file.sizeBytes)}
          </div>

          {mayChange ? (
            <>
              <div className="row" style={{ flexWrap: 'wrap' }}>
                <select
                  style={{ maxWidth: 130 }}
                  value={category}
                  onChange={(event) => setCategory(event.target.value as FileCategory)}
                >
                  {fileCategoryLabels.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
                <input
                  style={{ flex: 1, minWidth: 160 }}
                  placeholder="Billedtekst, f.eks. Tavle 2 efter udskiftning"
                  value={caption}
                  onChange={(event) => setCaption(event.target.value)}
                />
              </div>

              {error && <div className="error-box" style={{ marginTop: 10 }}>{error}</div>}

              <div className="btn-group" style={{ marginTop: 10 }}>
                <button className="btn btn--primary btn--sm" disabled={update.isPending} onClick={save}>
                  Gem
                </button>
                {file.webUrl && (
                  <a className="btn btn--sm" href={file.webUrl} target="_blank" rel="noreferrer">
                    Åbn i Teams
                  </a>
                )}
                <button className="btn btn--sm" disabled={remove.isPending} onClick={deleteFile}>
                  Slet
                </button>
                <button className="btn btn--sm" onClick={onClose}>
                  Luk
                </button>
              </div>
              <div className="tiny muted" style={{ marginTop: 6 }}>
                Slettede filer kan gendannes fra SharePoints papirkurv i 93 dage.
              </div>
            </>
          ) : (
            <>
              {file.caption && <div className="small">{file.caption}</div>}
              <div className="btn-group" style={{ marginTop: 10 }}>
                {file.webUrl && (
                  <a className="btn btn--sm" href={file.webUrl} target="_blank" rel="noreferrer">
                    Åbn i Teams
                  </a>
                )}
                <button className="btn btn--sm" onClick={onClose}>
                  Luk
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
